import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonToast,
} from "@ionic/react";

import s from "./WebPaymentLanding.module.scss";
import useWebPaymentLandingStore from "./store";
import { useWebPaymentLandingProvider } from "./provider";
import { WLDownloadButtons } from "./WLDownloadButtons";
import { getDownloadLandingVersion } from "../../utils/redirectDownload";
import { WLCOLORS } from "../../../Common/config/global";
import { arrowBackOutline, star } from "ionicons/icons";
import CompleteProfileModal from "./CompleteProfileModal";
import { lazy, Suspense, useEffect, useMemo } from "react";

const RedirectDownloadV2 = lazy(
  () => import("./RedirectDownload/RedirectDownload")
);

const RedirectDownloadV6 = lazy(
  () => import("./RedirectDownloadV6/RedirectDownload")
);

export const WebPaymentLanding = ({
  redirectToApp,
  onCloseAsModal,
  isOneOffEntryProp,
}: {
  redirectToApp?: boolean;
  onCloseAsModal?: () => void;
  isOneOffEntryProp?: boolean;
}) => {
  useWebPaymentLandingProvider(redirectToApp);

  const wldpVersionParam = new URLSearchParams(window.location.search).get(
    "wldpVersion"
  );
  const defaultVersionNoParam = useMemo(
    () => getDownloadLandingVersion(null),
    []
  );
  const trimmedParam = wldpVersionParam?.trim();
  const hasValidVersionParam = trimmedParam === "2" || trimmedParam === "6";
  const versionStr = hasValidVersionParam
    ? getDownloadLandingVersion(wldpVersionParam)
    : defaultVersionNoParam;
  const paramsRedirectDownloadVersion: 2 | 6 = versionStr === "2" ? 2 : 6;

  // Persist the sampled variant to the URL when no valid param was provided, so a
  // full page reload keeps the same variant instead of re-running the 50/50 draw
  // (which would split succeedSignUp attribution for the same user across V2/V6).
  useEffect(() => {
    if (hasValidVersionParam) return;
    const url = new URL(window.location.href);
    url.searchParams.set("wldpVersion", versionStr);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [hasValidVersionParam, versionStr]);

  const {
    signedUpName,
    pid,
    error,
    paymentMessage,
    paymentError,
    redirectToApp: redirectUserToDownload,
    isExpressCheckout,
    isOneOffEntry,
  } = useWebPaymentLandingStore();

  const emailAddress = new URLSearchParams(window.location.search).get(
    "emailAddress"
  );
  const phoneNumber = new URLSearchParams(window.location.search).get(
    "phoneNumber"
  );

  const userId = new URLSearchParams(window.location.search).get("userId");

  return (
    <IonPage className={s.wlDownloadApp}>
      <IonContent scrollY={true} fullscreen={true} className={cx(s.content)}>
        {isEmpty(paymentError) && !redirectUserToDownload ? (
          <>
            {paramsRedirectDownloadVersion === 2 ? (
              <Suspense>
                <RedirectDownloadV2 />
              </Suspense>
            ) : (
              <Suspense>
                <RedirectDownloadV6 />
              </Suspense>
            )}
          </>
        ) : (
          <div className={s.downloadAppContent}>
            <div className={s.main}>
              <IonButton
                fill="clear"
                className={s.backBtn}
                onClick={() => {
                  if (onCloseAsModal !== undefined) {
                    onCloseAsModal();
                  }
                }}
              >
                <IonIcon icon={arrowBackOutline} />
              </IonButton>

              <div
                className={cx(s.downloadContainer, {
                  [s.rediretMode]: !!redirectToApp,
                })}
              >
                <IonLabel
                  className={cx(
                    s.downloadTitle,
                    "win-header-4 bold winnersans white"
                  )}
                >
                  {paymentMessage || paymentError}
                </IonLabel>
                <IonLabel className="win-sub medium gilroy white">
                  {!isEmpty(paymentMessage) && isEmpty(paymentError)
                    ? "Open the Winners Locker app and login to access your account"
                    : !isEmpty(paymentError)
                      ? "Please contact customer support."
                      : ""}
                </IonLabel>
                {isEmpty(paymentError) && (
                  <div className={s.subDetails}>
                    {isOneOffEntry || isOneOffEntryProp ? (
                      <>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} />
                          Secured one-off entry on the current giveaways
                        </IonLabel>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} />
                          Gain access to unlocked more entries
                        </IonLabel>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} />
                          Included for the upcoming exclusive promos
                        </IonLabel>
                      </>
                    ) : (
                      <>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} /> Unlock
                          bonus entries to major prize giveaways
                        </IonLabel>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} /> Gain access
                          to exclusive perks and discounts
                        </IonLabel>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} /> Accumulate
                          in-app rewards points
                        </IonLabel>
                        <IonLabel className="win-sub medium gilroy white">
                          <IonIcon icon={star} className={s.star} /> Open your
                          daily locker for extra prizes
                        </IonLabel>
                      </>
                    )}
                    <IonLabel className="win-sub medium gilroy white">
                      <IonIcon icon={star} className={s.star} /> Plus much more
                      to come
                    </IonLabel>
                  </div>
                )}
                <WLDownloadButtons />
              </div>
            </div>
          </div>
        )}

        {isExpressCheckout && (
          <CompleteProfileModal
            emailAddress={emailAddress || ""}
            phoneNumber={phoneNumber || ""}
            fullName={signedUpName || ""}
            userId={userId || ""}
          />
        )}
      </IonContent>

      <IonToast
        message={error}
        isOpen={!isEmpty(error)}
        duration={2000}
        color={WLCOLORS.danger}
      />
    </IonPage>
  );
};
